// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import setIsUpdatingCheckout from "@bg/App/Handlers/OnePageCheckout/SetIsUpdatingCheckout";
import {
    addToChainBefore,
    addToEndOfChain,
    addToStartOfChain,
    makeHandlerChainAwaitable,
} from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import validateAddress, { AddressErrors } from "@insite/client-framework/Store/CommonHandlers/ValidateAddress";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    chain,
    ExecuteOnSuccessCallback,
} from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateBillTo";

const DispatchStartCheckoutLoading: typeof ExecuteOnSuccessCallback = props => {
    props.dispatch(setIsUpdatingCheckout({ isUpdatingCheckout: true }));
};

export const ValidateBillingAddress: typeof ExecuteOnSuccessCallback = async ({ dispatch, getState }) => {
    const state = getState();
    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart || !cart.billToId) {
        return;
    }

    const billTo = getBillToState(state, cart.billToId).value;
    if (!billTo) {
        return;
    }

    const billingAddressFormState = state.pages.onePageCheckout.billingAddressFormState;
    if (!billingAddressFormState) {
        return;
    }

    let addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        addressFieldsDataView = getAddressFieldsDataView(state);
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateAddress>[0], AddressErrors>(
        validateAddress,
    );
    const billingAddressErrors = await awaitableValidate({
        address: billTo,
        validationRules: billTo.validation!,
        addressFieldDisplayCollection: addressFieldsDataView.value!.billToAddressFields,
    })(dispatch, getState);
    const isBillingAddressValid = Object.keys(billingAddressErrors).length === 0;

    dispatch({
        type: "Pages/OnePageCheckout/SetIsBillingAddressUpdateRequired",
        isBillingAddressUpdateRequired: !isBillingAddressValid,
    });
};

export const ValidateShippingAddress: typeof ExecuteOnSuccessCallback = async ({ dispatch, getState }) => {
    const state = getState();

    const { cartId } = state.pages.onePageCheckout;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    if (!cart || !cart.billToId) {
        return;
    }

    const shipTo = getShipToState(state, cart.shipToId).value;
    if (!shipTo) {
        return;
    }

    const session = state.context.session;
    if (session.fulfillmentMethod === FulfillmentMethod.PickUp) {
        return;
    }

    let addressFieldsDataView = getAddressFieldsDataView(state);
    if (!addressFieldsDataView.value) {
        addressFieldsDataView = getAddressFieldsDataView(state);
    }

    const awaitableValidate = makeHandlerChainAwaitable<Parameters<typeof validateAddress>[0], AddressErrors>(
        validateAddress,
    );
    const shippingAddressErrors = await awaitableValidate({
        address: shipTo,
        validationRules: shipTo.validation!,
        addressFieldDisplayCollection: addressFieldsDataView.value!.shipToAddressFields,
    })(dispatch, getState);
    const isShippingAddressValid = Object.keys(shippingAddressErrors).length === 0;

    dispatch({
        type: "Pages/OnePageCheckout/SetIsShippingAddressUpdateRequired",
        isShippingAddressUpdateRequired: !isShippingAddressValid,
    });
};

const DispatchFinishCheckoutLoading: typeof ExecuteOnSuccessCallback = props => {
    props.dispatch(setIsUpdatingCheckout({ isUpdatingCheckout: false }));
};

addToStartOfChain(chain, DispatchStartCheckoutLoading);
addToChainBefore(chain, ExecuteOnSuccessCallback, ValidateBillingAddress);
addToChainBefore(chain, ValidateBillingAddress, ValidateShippingAddress);
addToEndOfChain(chain, DispatchFinishCheckoutLoading);
